import React, { useState, useCallback, useMemo } from 'react';
import { message } from 'antd';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import DataTable from '@/components/DataTable';
import Filter, { FilterConfig } from '@/components/Filter';
import { StatItem } from '@/components/StatsDisplay';
import { getUTXOColumns } from './columns';
import { isSupportedChainId, SupportedChains } from '@packet/constants/AppSupportChains';
import { TransactionBaseDto } from '@/types/commitment';
import { CommitmentDetail, getCommitmentList } from '@/api/commitment';
import { Hash } from 'viem';
import { useFilterAndPagination } from '@/hooks/useFilterAndPagination';
import { useChainStore } from '@/stores/chain';
import { useIsMobile } from '@packet/management';
import DownOutlined from '@/assets/icon/down-outlined.svg?react';
import { ChainConfigMap } from '@/const/chains';

// 页面标题
const PAGE_TITLE = 'UTXO';

// 筛选配置
const FILTER_CONFIGS: FilterConfig[] = [
  {
    key: 'chain_id',
    placeholder: 'Network',
    options: SupportedChains.map((chain) => ({
      value: chain.id,
      label: chain.name,
    })),
    defaultValue: SupportedChains[0].id, // 默认选择第一个网络
    defaultValueFn: () => {
      const chainId = useChainStore.getState().chainId;
      return chainId || SupportedChains[0].id;
    },
    allowClear: false, // 网络筛选器不允许清除，必须有选择
    minWidth: 220,
    changeClear: ['asset'],
  },
  {
    key: 'asset',
    placeholder: 'Token',
    options: [],
  },
  {
    key: 'state',
    placeholder: 'State',
    minWidth: 100,
    options: [
      { label: 'Active', value: 'Active' },
      { label: 'Spent', value: 'Spent' },
      { label: 'Frozen', value: 'Frozen' },
      { label: 'Thawed', value: 'Thawed' },
    ],
  },
];
// state 对应的搜索配置
const StateValueMap: Record<string, any> = {
  Active: { is_spent: false, is_frozen: false },
  Spent: { is_spent: true, is_frozen: false },
  Frozen: { is_spent: false, is_frozen: true },
  Thawed: { is_spent: true, is_frozen: true },
};

export default function UtxoManagement() {
  const navigate = useNavigate();
  const [utxos, setUtxos] = useState<CommitmentDetail[]>([]);
  const { isMobile } = useIsMobile();
  const [showMobileFilter, setShowMobileFilter] = useState(false);

  // 获取当前选择的链
  const chain = useMemo(() => {
    return (filters: any) => {
      const chooseId = parseInt(filters.chain_id as string);
      if (!isSupportedChainId(chooseId)) return SupportedChains[0];
      const chain = SupportedChains.find((chain) => chain.id === chooseId);
      return chain || SupportedChains[0];
    };
  }, []);

  // 数据加载函数
  const loadUtxos = useCallback(
    async (filterParams: any, paginationParams: { current: number; pageSize: number }) => {
      setLoading(true);
      try {
        // 获取网络配置
        let selectedChainId = Number(filterParams?.chain_id ? filterParams.chain_id : SupportedChains[0].id);
        if (!isSupportedChainId(selectedChainId)) selectedChainId = SupportedChains[0].id;
        const selectedChain = ChainConfigMap[selectedChainId as keyof typeof ChainConfigMap];
        const chain_id = selectedChainId;
        const exts: any = {};
        const stateValue = StateValueMap[filterParams.state];
        if (stateValue) {
          Object.assign(exts, stateValue);
        }
        const apiParams = {
          limit: paginationParams.pageSize,
          offset: (paginationParams.current - 1) * paginationParams.pageSize,
          address: selectedChain.appContract,
          chain_id,
          order: 'desc',
          ...exts,
        };

        // 添加时间范围筛选
        if (filterParams?.dateRange && Array.isArray(filterParams.dateRange) && filterParams.dateRange.length === 2) {
          const startDateStr = filterParams.dateRange[0];
          const endDateStr = filterParams.dateRange[1];
          if (startDateStr && endDateStr) {
            // 将日期字符串转换为时间戳（秒）
            apiParams.begin_time = Math.floor(new Date(startDateStr).getTime() / 1000);
            apiParams.end_time = Math.floor(new Date(endDateStr + ' 23:59:59').getTime() / 1000);
          }
        }

        if (filterParams?.asset) {
          apiParams.asset = filterParams.asset;
        }

        const commitments = await getCommitmentList(apiParams);

        if (!commitments.data) throw new Error('No data');
        setUtxos(commitments.data.data);
        setPaginationTotal(commitments.data.total);
      } catch (error) {
        message.error('Failed to load UTXOs');
      } finally {
        setLoading(false);
      }
    },
    [chain],
  );

  // 使用通用的筛选和分页hook
  const { filters, pagination, loading, handleFilterChange, dynamicFilterConfigs, setPaginationTotal, setLoading } = useFilterAndPagination({
    filterConfigs: FILTER_CONFIGS,
    defaultPageSize: 10,
    onDataLoad: loadUtxos,
    autoLoad: true,
  });

  // 获取当前选择的链实例
  const selectedChain = useMemo(() => chain(filters), [chain, filters]);

  // 处理操作按钮
  const onViewTransaction = (record: TransactionBaseDto<Hash>) => {
    navigate(`/transaction/${selectedChain.id}/${record.txhash}-${record.tx_index}`);
  };

  const onViewCommitment = (record: CommitmentDetail) => {
    navigate(`/utxo/${selectedChain.id}/${record.commitment}`);
  };

  // 计算统计数据
  const statsData: StatItem[] = [
    {
      label: 'Total Amount',
      value: '24,357',
    },
    {
      label: 'Total Value',
      value: '$1,357M',
    },
    {
      label: 'Frozen',
      value: '72',
    },
    {
      label: 'New Today',
      value: '1,355',
    },
  ];

  return (
    <PageContainer>
      <MainContent>
        {/* 页面标题和筛选器区域 */}
        <HeaderSection>
          <PageTitle>
            {PAGE_TITLE}

            {isMobile && (
              <MobileFilterButton onClick={() => setShowMobileFilter((prev) => !prev)}>
                Filter
                <ArrowIcon $open={showMobileFilter}>
                  <DownOutlined />
                </ArrowIcon>
              </MobileFilterButton>
            )}
          </PageTitle>
          <FilterSection>
            {(!isMobile || showMobileFilter) && (
              <Filter onFilterChange={handleFilterChange} initialFilters={filters} loading={loading} filterConfigs={dynamicFilterConfigs} showDateRange={true} showDownload={false} />
            )}
          </FilterSection>
        </HeaderSection>

        {/* 统计信息 */}
        {/* <StatsSection>
          <StatsDisplay stats={statsData} />
        </StatsSection> */}

        {/* 表格区域 */}
        <TableSection>
          <DataTable
            rowKey="commitment"
            data={utxos}
            columns={getUTXOColumns(selectedChain.id, onViewCommitment, onViewTransaction)}
            onRowClick={onViewCommitment}
            loading={loading}
            pagination={pagination}
            statsInfo={{
              label: 'More than',
              value: pagination.total,
            }}
          />
        </TableSection>
      </MainContent>
    </PageContainer>
  );
}

// Styled Components
const PageContainer = styled.div`
  background: #ffffff;
  @media (max-width: 768px) {
    padding: 0 16px;
    overflow-x: hidden;
  }
`;

const MainContent = styled.div`
  max-width: var(--pc-max-width);
  margin: 0 auto;
`;

const HeaderSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin: 40px 0 16px 0;

  @media (max-width: 1024px) {
    flex-direction: column;
    gap: 20px;
    align-items: flex-start;
  }
`;

const PageTitle = styled.h1`
  font-family: 'Poppins', sans-serif;
  font-size: 24px;
  font-weight: 600;
  color: #141413;
  margin: 0;
  line-height: 1;
  @media (max-width: 768px) {
    width: 100%;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
`;

const FilterSection = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;

  @media (max-width: 1024px) {
    flex-wrap: wrap;
    width: 100%;
  }
`;

const TableSection = styled.div`
  background: #ffffff;
  border-radius: 0;
  overflow: hidden;
`;

const MobileFilterButton = styled.button`
  display: inline-flex;
  height: 40px;
  padding: 8px 14px;
  justify-content: center;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
  border-radius: 10px;
  border: 1px solid rgba(16, 41, 0, 0.2);
  background: #fff;
`;

const ArrowIcon = styled.span<{ $open: boolean }>`
  display: inline-block;
  margin-left: 4px;
  font-size: 16px;
  transition: transform 0.2s;
  transform-origin: center;
  transform: ${({ $open }) => ($open ? 'rotate(180deg)' : 'none')};
`;
